/**
 * Позиция бюджетного справочника — заведение и правка.
 *
 * Ручной ввод по UC founder (шаг 7): «каждую позицию завожу и метчу с
 * системным справочником, указываю тип, название, ед.изм., цену». Колонки
 * рекомендуемых норм здесь нет — убрана правкой founder 15.08.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Stack } from '@/components/ui/stack';
import { EntityPicker, type EntityOption } from '@/pages/reference/pesticides/EntityPicker';
import {
  SECTION_TITLES,
  hasSystemCatalog,
  isFarmLevel,
  parsePriceInput,
  priceUnitSuffix,
  systemCatalogSource,
  useMeasurementUnits,
  type BudgetItem,
  type BudgetItemInput,
  type BudgetSection,
  type MatchStatus,
} from '@/lib/planning/budgetCatalog';
import { ItemKindSelect, hasItemKindOptions } from './ItemKindSelect';
import { CropKindSelect, resolveCropId } from './CropKindSelect';
import { useCropPicker } from '@/components/fields/card/api';
import { UnitSelect } from './UnitSelect';

/**
 * Подсказки резолвера под полем «Системный справочник».
 *
 * Зачем отдельный блок, если поиск по каталогу и так рядом: позиция попадает
 * сюда именно потому, что точного совпадения НЕ нашлось. Человек в этот момент
 * не знает, что искать — «Балиста» в каталоге записана «Баллиста», «Пенкоцеб»
 * как «Пеннкоцеб». Ткнуть в готовую подсказку — одно движение, а поиск с нуля
 * по 2390 препаратам — это то, из-за чего очередь разбора и не разбирают.
 *
 * Клик НЕ сохраняет: он подставляет эталон в поле, сохранение остаётся за
 * кнопкой диалога. Цена ошибки в СЗР высокая (не тот препарат = не те деньги и
 * не та химия), поэтому шаг подтверждения не срезаем.
 */
function MatchCandidates({
  candidates,
  onPick,
}: {
  candidates: BudgetItem['match_candidates'];
  onPick: (picked: EntityOption) => void;
}) {
  if (!candidates?.length) return null;
  // Точная неоднозначность и «похоже по буквам» — разный уровень доверия, и
  // говорить о них одинаково нельзя: в первом случае человек ВЫБИРАЕТ из двух
  // верных, во втором — ПРОВЕРЯЕТ догадку машины.
  const ambiguous = candidates.some((c) => c.why === 'exact_ambiguous');
  return (
    <div className="mt-2">
      <p className="text-xs text-muted-foreground">
        {ambiguous
          ? 'Имя совпало точно, но в каталоге несколько действующих регистраций — выберите нужную:'
          : 'Похожее в каталоге (проверьте, это догадка, а не совпадение):'}
      </p>
      <div className="mt-1 flex flex-wrap gap-2">
        {candidates.map((c) => (
          <Button
            key={c.id}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPick({ id: c.id, name: c.name })}
            title={
              c.why === 'exact_ambiguous'
                ? 'точное совпадение имени'
                : `похожесть ${Math.round(c.score * 100)}%`
            }
          >
            {c.name}
            {c.why === 'fuzzy' && (
              <span className="ml-2 text-muted-foreground">{Math.round(c.score * 100)}%</span>
            )}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Как называется «тип» в этом разделе. Слово из шапки справочника, а не
 * универсальное «Тип»: у работ это группа работ, у аутсорса — вид услуги.
 */
const KIND_LABELS: Partial<Record<BudgetSection, string>> = {
  work_rate: 'Группа работ',
  outsource: 'Вид услуги',
  seed: 'Культура',
};

export interface BudgetItemDialogSubmit {
  name: string;
  item_kind: string | null;
  grade: string | null;
  unit_id: string | null;
  planned_price: number | null;
  price_source: string | null;
  ref_id?: string | null;
  /**
   * Шлём, ТОЛЬКО когда человек явно распорядился статусом сопоставления
   * («своя позиция» поставили или сняли). В остальных случаях `undefined`:
   * бэкенд сам выведет статус из `ref_id`, и патч правки цены не должен
   * переписывать разбор очереди.
   */
  match_status?: MatchStatus;
}

export function BudgetItemDialog({
  section,
  item,
  farmId,
  farmName,
  saving,
  onSubmit,
  onClose,
}: {
  section: BudgetSection;
  /** null → заводим новую позицию. */
  item: BudgetItem | null;
  farmId: string | null;
  farmName: string | null;
  saving: boolean;
  onSubmit: (values: BudgetItemDialogSubmit) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [kind, setKind] = useState(item?.item_kind ?? '');
  const [grade, setGrade] = useState(item?.grade ?? '');
  const [unitId, setUnitId] = useState<string | null>(item?.unit_id ?? null);
  const [price, setPrice] = useState(
    item?.planned_price === null || item?.planned_price === undefined
      ? ''
      : String(item.planned_price),
  );
  const [priceSource, setPriceSource] = useState(item?.price_source ?? '');
  const [match, setMatch] = useState<EntityOption | null>(null);
  /**
   * Трогали ли поле «Системный справочник» в этом сеансе правки.
   *
   * Без явного признака сброс сопоставления не доезжал до БД (вердикт head-web
   * по #853, блокер 1): патч слал `ref_id`, только когда тот истинный, и
   * «сменил пшеницу на подсолнечник → сохранил» оставляло позицию
   * сопоставленной с сортом ПШЕНИЦЫ. Отличать «сбросили» от «не открывали»
   * обязательно: голое `ref_id: null` в каждом патче затирало бы чужое
   * сопоставление при правке одного только названия.
   */
  const [matchTouched, setMatchTouched] = useState(false);
  // «Эталона нет» — легальный исход разбора, а не незакрытая задача. Без него
  // очередь неопустошима: прилипатели, биостимуляторы и инокулянты в реестре
  // СЗР не значатся в принципе, и «?» на них висел бы вечно.
  const ownInitial = item?.match_status === 'own';
  const [own, setOwn] = useState(ownInitial);
  const crops = useCropPicker(section === 'seed');
  /**
   * Системная культура выбранного «типа» — ТОЛЬКО для фильтра сортов (AK-704).
   * В БД не едет: `item_kind` остаётся текстом, миграции нет.
   *
   * Считается ИЗ НАЗВАНИЯ, а не хранится отдельным состоянием: у сохранённой
   * позиции в БД лежит текст, и состояние стартовало бы пустым — поле сорта
   * оставалось бы закрытым, пока человек заново не выберет ту же культуру
   * (вердикт head-web по #853). «Своя» культура вне номенклатуры даёт `null` —
   * сужать сорта нечем, и это честно видно на экране.
   */
  const cropId = useMemo(
    () => (section === 'seed' ? resolveCropId(crops.data ?? [], kind) : null),
    [section, crops.data, kind],
  );
  const units = useMeasurementUnits();
  const selectedUnit = units.data?.find((u) => u.id === unitId) ?? null;

  const kindLabel = KIND_LABELS[section] ?? 'Тип';
  const source = systemCatalogSource(section);
  // Есть ли у раздела системный каталог ВООБЩЕ — это не то же самое, что «есть
  // чем его листать». У удобрений каталог есть (reference.fertilizers, 460
  // строк, бэкенд пишет туда ref_id), а REST-эндпоинта на него нет.
  const catalogExists = hasSystemCatalog(section);
  const parsedPrice = parsePriceInput(price);
  const priceInvalid = parsedPrice === undefined;
  const canSave = name.trim().length > 0 && !priceInvalid && !saving;

  const scopeHint = isFarmLevel(section)
    ? farmId
      ? `Ведётся в хозяйстве «${farmName ?? 'выбранное'}»`
      : 'Ведётся в шаблоне компании — из него расценки копируются в хозяйства'
    : 'Закупки ведутся на компанию: цена едина для всех хозяйств, исключение заводится строкой «цена для хозяйства»';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {item ? 'Позиция бюджета' : 'Новая позиция бюджета'} — {SECTION_TITLES[section]}
          </DialogTitle>
          <DialogDescription>{scopeHint}</DialogDescription>
        </DialogHeader>

        <Stack gap={3}>
          <div>
            <Label htmlFor="bc-name">Наименование в бюджете</Label>
            <Input
              id="bc-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Как эта позиция называется у вас"
            />
          </div>

          {section !== 'sale_product' && (
            <div>
              {/* `htmlFor` только там, где реально есть <input id="bc-kind">:
                  у семян теперь выпадашка, и подпись указывала в пустоту. */}
              {hasItemKindOptions(section) || section === 'seed' ? (
                <Label>{kindLabel}</Label>
              ) : (
                <Label htmlFor="bc-kind">{kindLabel}</Label>
              )}
              {hasItemKindOptions(section) ? (
                // Выпадашка с поиском и возможностью ввести своё (AK-656):
                // свободный текст плодил «гербицид / Гербициды / герб.».
                <ItemKindSelect section={section} value={kind} onChange={setKind} label={kindLabel} />
              ) : section === 'seed' ? (
                // Семена: «тип» здесь — КУЛЬТУРА, и у неё есть справочник
                // (номенклатура хозяйства). Свободный текст плодил «пшеница
                // озимая / Пшеница оз. / оз.пшеница» ровно как у типов СЗР до
                // AK-656. Заодно отдаёт id культуры — им сужается выбор сорта.
                <CropKindSelect
                  value={kind}
                  onChange={(cropName) => {
                    if (cropName === kind) return; // тот же выбор — не трогаем сорт
                    setKind(cropName);
                    // Сорт принадлежит культуре: сменили культуру — прежний
                    // сорт больше не её, и оставлять его значит сохранить
                    // заведомо неверное сопоставление. `matchTouched` доносит
                    // этот сброс до патча — иначе он живёт только на экране.
                    setMatch(null);
                    setMatchTouched(true);
                  }}
                  label={kindLabel}
                />
              ) : (
                <Input
                  id="bc-kind"
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  placeholder={`например: ${kindLabel.toLowerCase()}`}
                />
              )}
            </div>
          )}

          {section === 'sale_product' && (
            <div>
              <Label htmlFor="bc-grade">Класс качества</Label>
              <Input
                id="bc-grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder="например: 3 класс, фураж, базис"
              />
            </div>
          )}

          <div>
            <Label>Системный справочник</Label>
            {catalogExists ? (
              <>
                {source ? (
                <EntityPicker
                  source={{
                    path: source.path,
                    translated: source.translated,
                    // Сорта — только выбранной культуры (AK-704). Без этого
                    // агроном искал сорт пшеницы среди сортов всех культур
                    // сразу, и поле выглядело мёртвым.
                    params: section === 'seed' && cropId ? { crop_id: cropId } : undefined,
                  }}
                  value={match}
                  onChange={(picked) => {
                    setMatch(picked);
                    setMatchTouched(true);
                  }}
                  disabled={own || (section === 'seed' && !cropId)}
                  placeholder={`Найти: ${source.label}`}
                />
                ) : (
                  // Каталог у раздела ЕСТЬ (бэкенд пишет ref_id и показывает имя
                  // эталона), а листать его из UI пока нечем: REST-эндпоинта на
                  // reference.fertilizers не существует. Раньше это выглядело как
                  // «справочника нет» — и противоречило галке «сопоставлено» в
                  // той же строке. Пока эндпоинт не появился, разбор идёт по
                  // подсказкам резолвера ниже.
                  <p className="text-sm text-muted-foreground">
                    Поиск по каталогу удобрений из этого окна пока не подключён — выберите
                    из подсказок ниже или отметьте позицию своей.
                  </p>
                )}
                {source && section === 'seed' && !cropId && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Сначала выберите культуру — сорта показываем только её.
                  </p>
                )}
                {item?.ref_id && !match && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Позиция уже сопоставлена. Выберите другой эталон, только если хотите
                    заменить сопоставление.
                  </p>
                )}
                {!own && (
                  <MatchCandidates
                    candidates={item?.match_candidates ?? null}
                    onPick={(picked) => {
                      setMatch(picked);
                      setMatchTouched(true);
                    }}
                  />
                )}
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={own}
                    onCheckedChange={(v) => {
                      const next = v === true;
                      setOwn(next);
                      if (next) {
                        // Снимаем эталон вместе с отметкой: «своя позиция» и
                        // ссылка на справочник — взаимоисключающие исходы.
                        setMatch(null);
                        setMatchTouched(true);
                      }
                    }}
                    aria-label="Эталона нет — ведём как свою позицию"
                  />
                  <span>Эталона в справочнике нет — ведём как свою позицию</span>
                </label>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                У этого раздела системного справочника нет — позиция ведётся как своя.
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="bc-price">Плановая цена, ₽{priceUnitSuffix(selectedUnit)}</Label>
              <Input
                id="bc-price"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                aria-invalid={priceInvalid || undefined}
                className="text-right tabular-nums"
                placeholder="0"
              />
              {priceInvalid && (
                <p className="mt-1 text-xs text-destructive">
                  Цена — число не меньше нуля.
                </p>
              )}
            </div>
            <div className="flex-1">
              <Label>Единица измерения</Label>
              <UnitSelect value={unitId} onChange={setUnitId} />
            </div>
          </div>

          <div>
            <Label htmlFor="bc-src">Источник цены</Label>
            <Input
              id="bc-src"
              value={priceSource}
              onChange={(e) => setPriceSource(e.target.value)}
              placeholder="договор, прайс, коммерческое предложение…"
            />
          </div>
        </Stack>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            disabled={!canSave}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                item_kind: kind.trim() || null,
                grade: grade.trim() || null,
                unit_id: unitId,
                planned_price: parsedPrice ?? null,
                price_source: priceSource.trim() || null,
                // `undefined` — пикер не трогали, патч поле не шлёт (гард от
                // случайного сброса при правке одного названия). `null` —
                // сопоставление СНЯЛИ осознанно, и это обязано доехать до БД.
                ref_id: match ? match.id : matchTouched ? null : undefined,
                // Статус шлём только при явном распоряжении человека: отметку
                // поставили (own) либо сняли (ownInitial, и тогда статус обязан
                // вернуться к обычному разбору — сам бэкенд из `own` не выйдет).
                match_status: own ? 'own' : ownInitial ? (match ? 'matched' : 'queued') : undefined,
              })
            }
          >
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Собрать тело ПАТЧА позиции из значений формы.
 *
 * Существует отдельно от `toCreateBody` ради одного решения: поле `ref_id`
 * попадает в патч, только если «Системный справочник» в этом сеансе ТРОГАЛИ.
 *
 *  * `undefined` → ключа в патче нет. Гард от случайного сброса: правка одного
 *    названия не должна снимать сопоставление, которое человек не открывал.
 *  * `null` → ключ есть со значением `null`. Сопоставление сняли осознанно —
 *    например сменили культуру у семян, и прежний сорт больше не её.
 *
 * Прежний гард был `ref_id ? {...} : rest` и глотал ВТОРОЙ случай: на экране
 * сорт исчезал, а в БД позиция оставалась привязанной к сорту прежней культуры
 * (вердикт head-web по #853, блокер 1). Проверка на `undefined` — та же защита,
 * но различающая «не трогали» и «сняли».
 */
export function toPatchBody(values: BudgetItemDialogSubmit): Partial<BudgetItemInput> {
  const { ref_id, ...rest } = values;
  return ref_id !== undefined ? { ...rest, ref_id } : rest;
}

/** Собрать тело создания позиции из значений формы. */
export function toCreateBody(
  values: BudgetItemDialogSubmit,
  seasonId: string,
  farmId: string | null,
): BudgetItemInput {
  return {
    season_id: seasonId,
    farm_id: farmId,
    name: values.name,
    item_kind: values.item_kind,
    grade: values.grade,
    unit_id: values.unit_id,
    planned_price: values.planned_price,
    price_source: values.price_source,
    ref_id: values.ref_id ?? null,
  };
}
